import { useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  STANDARD_SUBJECT_LABELS,
  gradeLabel,
  parseStandardsHtml,
  serializeStandards,
  type Standard,
  type StandardRef,
} from '@td/shared';
import { useStandards } from '../hooks/useStandards';

/**
 * The Standards field: picked standards as removable chips, plus a search box
 * that filters the grade's standards as you type.
 *
 * The results panel expands inline rather than floating. The cell editor is a
 * scrollable modal, and an absolutely-positioned dropdown gets clipped by that
 * scroll container — inline costs a little polish and always works.
 */
export function StandardsField({
  grade,
  value,
  onChange,
}: {
  grade: string | null;
  value: string;
  onChange: (html: string) => void;
}) {
  const { data: standards, isLoading } = useStandards(grade);
  const [query, setQuery] = useState('');
  const [subject, setSubject] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(() => parseStandardsHtml(value), [value]);
  const selectedCodes = useMemo(
    () => new Set(selected.map((s) => s.code).filter(Boolean)),
    [selected],
  );

  /** The subjects this grade actually has standards for. */
  const subjects = useMemo(() => {
    const present = new Set((standards ?? []).map((s) => s.subject));
    return Object.keys(STANDARD_SUBJECT_LABELS).filter((key) => present.has(key));
  }, [standards]);

  const results = useMemo(() => {
    const needle = query.trim().toLowerCase();

    const pool = (standards ?? [])
      .filter((s) => !selectedCodes.has(s.code))
      .filter((s) => !subject || s.subject === subject);

    if (!needle) return pool;

    /*
     * Every word has to appear somewhere, but not together — so "add ten"
     * finds "add within ten". Searching for the whole phrase only matched
     * standards that used those words side by side, which is almost none of
     * them.
     */
    const words = needle.split(/\s+/).filter(Boolean);
    const haystack = (s: Standard) => `${s.code} ${s.domain} ${s.text}`.toLowerCase();

    return pool.filter((s) => {
      const text = haystack(s);
      return words.every((w) => text.includes(w));
    });
  }, [standards, query, subject, selectedCodes]);

  /*
   * Grouped by domain for reading, kept flat for the arrow keys.
   *
   * The cap used to be forty results, which quietly hid most of a subject —
   * picking "Mathematics" showed a fraction of the maths standards with no
   * hint there were more. Browsing a subject is the whole point of the
   * filter, so the list is complete and simply scrolls.
   */
  const grouped = useMemo(() => {
    const byDomain = new Map<string, Standard[]>();
    for (const s of results) {
      const list = byDomain.get(s.domain) ?? [];
      list.push(s);
      byDomain.set(s.domain, list);
    }
    return [...byDomain.entries()];
  }, [results]);

  function commit(next: StandardRef[]) {
    onChange(serializeStandards(next));
  }

  function add(standard: Standard) {
    commit([...selected, { code: standard.code, text: standard.text }]);
    // The subject filter stays put: adding one standard from a subject is
    // usually followed by adding another from the same one.
    setQuery('');
    setHighlight(0);
    inputRef.current?.focus();
  }

  function addFreeText() {
    const text = query.trim();
    if (!text) return;
    commit([...selected, { code: null, text }]);
    setQuery('');
    inputRef.current?.focus();
  }

  function remove(index: number) {
    commit(selected.filter((_, i) => i !== index));
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setHighlight((h) => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = results[highlight];
      if (open && pick) add(pick);
      else addFreeText();
    } else if (e.key === 'Escape') {
      setOpen(false);
      setQuery('');
    } else if (e.key === 'Backspace' && !query && selected.length > 0) {
      // Backspace on an empty box removes the last chip, as tag inputs do.
      remove(selected.length - 1);
    }
  }

  return (
    <div>
      {selected.length > 0 && (
        <ul className="mb-2 space-y-1.5">
          {selected.map((s, i) => (
            <li
              key={`${s.code ?? 'text'}-${i}`}
              className="group flex items-start gap-2 rounded-lg border border-sand/70 bg-linen/40 px-2.5 py-1.5"
            >
              {s.code && (
                <span className="mt-px shrink-0 rounded bg-terracotta/12 px-1.5 py-0.5 text-[11px] text-terracotta">
                  {s.code}
                </span>
              )}
              <span className="min-w-0 flex-1 text-xs leading-snug text-walnut">{s.text}</span>
              <button
                type="button"
                onClick={() => remove(i)}
                aria-label={`Remove ${s.code ?? s.text}`}
                className="shrink-0 text-stone opacity-0 transition-opacity hover:text-rust group-hover:opacity-100 focus:opacity-100"
              >
                ✕
              </button>
            </li>
          ))}
        </ul>
      )}

      {!grade ? (
        <p className="rounded-lg border border-dashed border-sand px-3 py-2 text-xs text-stone">
          Set your grade level in{' '}
          <Link to="/settings" className="text-terracotta underline">
            Settings
          </Link>{' '}
          and your standards will be searchable here.
        </p>
      ) : (
        <>
          {/*
            Subject first, then search.
            
            Browsing by subject is how a teacher actually finds a standard —
            she knows it is a maths one — and typing a word was the only way
            in before. Only the subjects this grade has are offered.
          */}
          {subjects.length > 1 && (
            <div className="mb-1.5 flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => {
                  setSubject('');
                  setOpen(true);
                  setHighlight(0);
                }}
                aria-pressed={subject === ''}
                className={subject === '' ? 'btn-soft-accent' : 'btn-soft'}
              >
                All subjects
              </button>

              {subjects.map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => {
                    setSubject(key);
                    setOpen(true);
                    setHighlight(0);
                  }}
                  aria-pressed={subject === key}
                  className={subject === key ? 'btn-soft-accent' : 'btn-soft'}
                >
                  {STANDARD_SUBJECT_LABELS[key] ?? key}
                </button>
              ))}
            </div>
          )}

          <div className="relative">
            <input
              ref={inputRef}
              className="field pl-8"
              placeholder={
                selected.length > 0 ? 'Add another standard…' : 'Search by code or wording…'
              }
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                setHighlight(0);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={onKeyDown}
              role="combobox"
              aria-expanded={open}
              aria-controls="standards-results"
              aria-label="Search standards"
            />
            <span className="pointer-events-none absolute left-2.5 top-2.5 text-sm text-stone">
              ⌕
            </span>
            {open && (
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  setQuery('');
                }}
                aria-label="Close results"
                className="absolute right-2 top-2 text-xs text-stone hover:text-espresso"
              >
                ✕
              </button>
            )}
          </div>

          {open && (
            <div
              id="standards-results"
              role="listbox"
              className="mt-1 max-h-64 overflow-y-auto rounded-lg border border-sand bg-white"
            >
              {isLoading ? (
                <p className="px-3 py-2 text-xs text-stone">Loading…</p>
              ) : results.length === 0 ? (
                <div className="px-3 py-2">
                  <p className="text-xs text-stone">
                    {query.trim()
                      ? `Nothing in ${subject ? (STANDARD_SUBJECT_LABELS[subject] ?? subject) : gradeLabel(grade)} matches “${query.trim()}”.`
                      : 'Every standard for this grade is already added.'}
                  </p>
                  {query.trim() && (
                    <button type="button" onClick={addFreeText} className="btn-soft-accent mt-1">
                      Add “{query.trim()}” as your own text
                    </button>
                  )}
                </div>
              ) : (
                grouped.map(([domain, items]) => (
                  <div key={domain}>
                    {/* A heading per domain, so a subject reads as a list of
                        topics rather than two hundred numbered lines. */}
                    <p className="sticky top-0 bg-linen px-3 py-1 text-[10px] uppercase tracking-wide text-stone">
                      {domain}
                    </p>

                    {items.map((s) => {
                      const i = results.indexOf(s);
                      return (
                        <button
                          key={s.code}
                          type="button"
                          role="option"
                          aria-selected={i === highlight}
                          onMouseEnter={() => setHighlight(i)}
                          onClick={() => add(s)}
                          className={[
                            'block w-full border-b border-sand/50 px-3 py-2 text-left last:border-b-0 transition-colors',
                            i === highlight ? 'bg-linen' : 'hover:bg-linen/60',
                          ].join(' ')}
                        >
                          <span className="flex items-baseline gap-1.5">
                            <span className="text-[11px] text-terracotta">{s.code}</span>
                            {s.essential && (
                              <span
                                className="rounded bg-sage/25 px-1 text-[9px] uppercase tracking-wide text-olive"
                                title="Essential for mastery by the end of the grade"
                              >
                                essential
                              </span>
                            )}
                            <span className="ml-auto shrink-0 text-[10px] text-stone">
                              {STANDARD_SUBJECT_LABELS[s.subject] ?? s.subject} · {s.domain}
                            </span>
                          </span>
                          <span className="mt-0.5 block text-xs leading-snug text-walnut">
                            {s.text}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>
          )}

          {open && results.length > 0 && (
            <p className="mt-1 text-[10px] text-stone">
              {results.length} standard{results.length === 1 ? '' : 's'} · ↑↓ to move · Enter to add
              · Esc to close
            </p>
          )}
        </>
      )}
    </div>
  );
}
